import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import logoDark from "@/assets/wtb-logo-dark.png.asset.json";
import { PasswordField } from "@/components/PasswordField";
import { passwordStrength } from "@/lib/password-strength";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/lib/auth-context";
import { checkUsernameAvailable } from "@/lib/profile.functions";
import { getMyTotpStatus, verifyTotpChallenge } from "@/lib/totp.functions";

const TCG_OPTIONS = [
  "Pokémon",
  "One Piece",
  "Magic: The Gathering",
  "Yu-Gi-Oh!",
  "Lorcana",
  "Digimon",
  "Other",
] as const;

const usernameRe = /^[a-z0-9_]{3,24}$/;

const authSearchSchema = z.object({
  redirect: z.string().optional(),
  verify: z.string().optional(),
});

function safeRedirect(target: string | undefined): string {
  if (!target) return "/dashboard";
  // Only allow same-origin relative paths (must start with single "/")
  if (!target.startsWith("/") || target.startsWith("//")) return "/dashboard";
  return target;
}

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: (s) => authSearchSchema.parse(s),
  head: () => ({ meta: [{ title: "Sign in — Want To Buy" }] }),
  component: Auth,
});

type Step = "credentials" | "twofa" | "forgot";

function Auth() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const redirectTo = safeRedirect(search.redirect);
  const { user, loading } = useAuth();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [country, setCountry] = useState("");
  const [tcgs, setTcgs] = useState<string[]>([]);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [twofaCode, setTwofaCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);

  // Redirect already-signed-in users away from /auth
  useEffect(() => {
    if (!loading && user && step === "credentials") {
      navigate({ to: redirectTo });
    }
  }, [user, loading, step, navigate, redirectTo]);

  // Show a notice when the auth gate bounced an unverified user here.
  useEffect(() => {
    if (search.verify === "pending") {
      toast.info("Please confirm your email before continuing. Check your inbox for the verification link.");
    }
  }, [search.verify]);


  async function onCredentialsSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!z.string().email().safeParse(email).success) {
      toast.error("Enter a valid email");
      return;
    }
    if (!password) {
      toast.error("Enter your password");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "signup") {
        if (!agreed) throw new Error("You must agree to the Terms and policies");
        if (!usernameRe.test(username)) {
          throw new Error("Username must be 3–24 chars: letters, numbers, underscore");
        }
        if (!passwordStrength(password).allPassed) {
          throw new Error("Password does not meet all requirements");
        }
        // Check availability (server-side normalized check)
        const avail = await checkUsernameAvailable({ data: { username } });
        if (!avail.available) throw new Error("Username is taken");

        const { data: signUpData, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/dashboard`,
            data: {
              username: username.toLowerCase(),
              display_name: displayName || null,
              country: country || null,
              tcg_specialisations: tcgs,
            },
          },
        });
        if (error) throw error;
        // Always require email confirmation before access. If Supabase
        // returned a session (auto-confirm or legacy config), drop it so
        // the user must click the verification link.
        if (signUpData.session) {
          await supabase.auth.signOut();
        }
        toast.success("Check your email to confirm your account before signing in.");
        setMode("signin");
        setPassword("");
        return;
      }

      // Sign in
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      // Block sign-in until email is confirmed.
      const { data: userData } = await supabase.auth.getUser();
      const u = userData.user;
      if (u && !u.email_confirmed_at && !u.confirmed_at) {
        await supabase.auth.signOut();
        throw new Error("Please confirm your email before signing in. Check your inbox for the verification link.");
      }


      // 2FA gate
      const status = await getMyTotpStatus();
      if (status.enrolled) {
        setStep("twofa");
      } else {
        toast.success("Welcome back");
        navigate({ to: redirectTo });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmit2FA(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await verifyTotpChallenge({ data: { code: twofaCode.trim(), backup: useBackup } });
      toast.success("Welcome back");
      navigate({ to: redirectTo });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Invalid code");
    } finally {
      setSubmitting(false);
    }
  }

  async function onGoogle() {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + redirectTo,
    });
    if (result.error) toast.error(result.error.message ?? "Google sign-in failed");
  }

  async function onForgot(e: React.FormEvent) {
    e.preventDefault();
    if (!z.string().email().safeParse(email).success) {
      toast.error("Enter your account email");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSubmitting(false);
    if (error) toast.error(error.message);
    else {
      toast.success("Check your email for a reset link");
      setStep("credentials");
    }
  }

  return (
    <div
      className="relative min-h-screen grid place-items-center p-6"
      style={{
        background:
          "radial-gradient(at 20% 0%, oklch(0.35 0.2 305) 0%, transparent 50%), radial-gradient(at 80% 100%, oklch(0.3 0.18 290) 0%, transparent 50%), oklch(0.16 0.04 280)",
      }}
    >
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-3">
          <img src={logoDark.url} alt="Want To Buy" className="h-12 w-12 rounded-md object-cover shadow-lg" />
          <span className="font-display text-2xl font-bold text-white">Want To Buy</span>
        </Link>
        <div className="panel p-6 sm:p-8 shadow-2xl">
          {step === "credentials" && (
            <>
              <div className="mb-5 grid grid-cols-2 rounded-md border border-border bg-background/60 p-1 text-sm font-semibold">
                <button type="button" onClick={() => setMode("signin")} className={`rounded py-2 transition ${mode === "signin" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Sign in</button>
                <button type="button" onClick={() => setMode("signup")} className={`rounded py-2 transition ${mode === "signup" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Create account</button>
              </div>

              <h1 className="font-display text-2xl font-bold">{mode === "signin" ? "Welcome back" : "Create your account"}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {mode === "signin" ? "Sign in to post buy orders and submit offers." : "Start posting buy orders in minutes."}
              </p>

              <form onSubmit={onCredentialsSubmit} className="mt-6 space-y-3">
                {mode === "signup" && (
                  <>
                    <TextInput
                      label="Username (your public @handle)"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                      placeholder="cardking99"
                      maxLength={24}
                      autoComplete="username"
                      required
                    />
                    <TextInput
                      label="Display name (optional)"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Jane Doe"
                      maxLength={40}
                    />
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Country (optional)
                      </span>
                      <input
                        value={country}
                        onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
                        placeholder="CA"
                        maxLength={2}
                        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm uppercase focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <span className="mt-1 block text-[11px] text-muted-foreground">Two-letter ISO code. You can add this later.</span>
                    </label>
                    <div>
                      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        TCGs you trade (optional)
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {TCG_OPTIONS.map((t) => {
                          const on = tcgs.includes(t);
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => setTcgs((prev) => (on ? prev.filter((x) => x !== t) : [...prev, t]))}
                              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/60"}`}
                            >
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
                <TextInput
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
                {mode === "signup" ? (
                  <PasswordField label="Password" value={password} onChange={setPassword} />
                ) : (
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </label>
                )}
                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={() => setStep("forgot")}
                    className="text-right block w-full text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
                {mode === "signup" && (
                  <label className="flex items-start gap-2.5 rounded-md border border-border bg-background/40 p-3 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-primary"
                      required
                    />
                    <span>
                      I have read and agree to the <Link to="/terms" className="text-primary hover:underline">Terms and Conditions</Link>, <Link to="/platform-policies" className="text-primary hover:underline">Platform Policies</Link>, <Link to="/seller-policy" className="text-primary hover:underline">Seller Policy</Link>, and <Link to="/rating-policy" className="text-primary hover:underline">Rating &amp; Strike Policy</Link>. I understand that selling counterfeit or misrepresented cards results in an immediate permanent ban with no appeal. <strong>I also consent to Want To Buy administrators and moderators accessing my private messages on the platform for the purposes of investigating policy violations, resolving disputes, detecting fraud, and enforcing platform rules.</strong>
                    </span>
                  </label>
                )}
                <button disabled={submitting} className="btn-primary mt-2 w-full py-2.5 text-sm disabled:opacity-60">
                  {submitting ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
                </button>
              </form>

              <div className="my-4 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
                <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
              </div>
              <button onClick={onGoogle} className="btn-ghost flex w-full items-center justify-center gap-2 py-2.5 text-sm">
                <GoogleIcon /> Continue with Google
              </button>
              <p className="mt-6 text-center text-[11px] text-muted-foreground">
                By continuing, you agree to our <Link to="/terms" className="hover:underline">Terms</Link> and <Link to="/seller-policy" className="hover:underline">Seller Policy</Link>. Payments flow directly buyer → seller via PayPal Goods &amp; Services.
              </p>
            </>
          )}

          {step === "twofa" && (
            <>
              <h1 className="font-display text-2xl font-bold">Two-factor authentication</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {useBackup ? "Enter one of your 8-character backup codes." : "Enter the 6-digit code from your authenticator app."}
              </p>
              <form onSubmit={onSubmit2FA} className="mt-6 space-y-3">
                <input
                  autoFocus
                  inputMode={useBackup ? "text" : "numeric"}
                  maxLength={useBackup ? 9 : 6}
                  value={twofaCode}
                  onChange={(e) => setTwofaCode(useBackup ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))}
                  placeholder={useBackup ? "XXXX-XXXX" : "123456"}
                  className="h-12 w-full rounded-md border border-border bg-background px-3 text-center text-lg tracking-[0.4em] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button disabled={submitting} className="btn-primary mt-2 w-full py-2.5 text-sm disabled:opacity-60">
                  {submitting ? "Verifying…" : "Verify and continue"}
                </button>
                <button type="button" onClick={() => { setUseBackup((v) => !v); setTwofaCode(""); }} className="block w-full text-center text-xs text-primary hover:underline">
                  {useBackup ? "Use authenticator code instead" : "Use a backup code instead"}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await supabase.auth.signOut();
                    setStep("credentials");
                    setTwofaCode("");
                    setUseBackup(false);
                  }}
                  className="block w-full text-center text-[11px] text-muted-foreground hover:text-foreground"
                >
                  ← Back to sign in
                </button>
              </form>
            </>
          )}

          {step === "forgot" && (
            <>
              <h1 className="font-display text-2xl font-bold">Reset your password</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                We'll email you a link to set a new password.
              </p>
              <form onSubmit={onForgot} className="mt-6 space-y-3">
                <TextInput
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
                <button disabled={submitting} className="btn-primary w-full py-2.5 text-sm disabled:opacity-60">
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
                <button type="button" onClick={() => setStep("credentials")} className="block w-full text-center text-xs text-muted-foreground hover:text-foreground">
                  ← Back to sign in
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TextInput({ label, ...rest }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <input {...rest} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30" />
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83Z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.3 9.14 5.38 12 5.38Z"/>
    </svg>
  );
}
